import React, {useEffect} from 'react'
import {FilterIcon, BookmarkIcon} from '@primer/octicons-react'
import {Button, Token} from '@primer/react'
import {clsx} from 'clsx'

import styles from './SearchSubHeader.module.css'
import ExtraMenu from './ExtraMenu'

import {Dialog} from '@primer/react/experimental'
import {
  CodeSortItems,
  CommitSortItems,
  DiscussionSortItems,
  FocusHintPrefix,
  IssueSortItems,
  PackageSortItems,
  RepoSortItems,
  type SearchResults,
  UserSortItems,
  WikiSortItems,
} from '../types/blackbird-types'
import {CountMode, getCountText} from '@github-ui/count'
import {useLocation} from '@github-ui/react-router'

import {FacetsDialogContent} from './FacetsPane'
import SortMenu from './SortMenu'
import TypeMobileDropdown from './FacetsPane/TypeMobileDropdown'
import {parseString} from '@github-ui/blackbird-query-parsing/parsing'
import type {ContentNode, NodeWithChildren, QualifierNode} from '@github/blackbird-parser'
import {useNavigateToQuery} from '../hooks/use-navigate-to-query'
import type {SearchKindCount} from '../hooks/use-search-result-counts'
import {ExperimentsDialog} from './ExperimentsDialog'
import {useFocusHint} from '@github-ui/focus-hint-context'
import {useLoggedInContext} from '../contexts/LoggedInContext'
import {debugScoringInfoEnabled} from '@github-ui/blackbird-experiments'

export function SearchSubHeader({
  payload,
  selectedType,
  searchKinds,
  isLoadingCounters,
  isLoading,
}: {
  payload: SearchResults
  selectedType: string | undefined
  searchKinds: SearchKindCount[]
  isLoadingCounters: boolean
  isLoading: boolean
}) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [openExperimentsDialog, setOpenExperimentsDialog] = React.useState(false)
  const openDialog = React.useCallback(() => setIsOpen(true), [setIsOpen])
  const closeDialog = React.useCallback(() => setIsOpen(false), [setIsOpen])
  const loggedIn = useLoggedInContext()
  const navigateToQuery = useNavigateToQuery()

  const sortItems = getSortItemsForType(payload.type)
  const isCode = payload.type === 'code'
  const isLegacyCode = payload.type === 'codelegacy'
  const isAnyCode = isCode || isLegacyCode

  const openCustomScopesDialog = (event: React.SyntheticEvent) => {
    const e = new CustomEvent('blackbird_monolith_save_query_as_custom_scope', {detail: event.currentTarget})
    window.dispatchEvent(e)
  }

  const location = useLocation()
  const urlSearchParams = new URLSearchParams(location.search)
  const query = urlSearchParams.get('q') || ''
  const searchKindCount = searchKinds?.find(kind => kind.name === selectedType) || searchKinds[0]!
  const showDebugScoringInfo = debugScoringInfoEnabled()

  const {focusHint} = useFocusHint()
  useEffect(() => {
    // focus on the search result count on page load
    if (!isLoading) {
      const focusElement = document.getElementById('search-results-count')
      if (!focusHint?.startsWith(FocusHintPrefix)) {
        focusElement?.focus()
      }
    }
  })

  return (
    <div data-testid="search-sub-header">
      <div className={styles.headerRow}>
        {loggedIn || !isCode ? (
          <ResultCount
            isLoadingCounters={isLoadingCounters}
            elapsedMilliseconds={payload.elapsed_millis}
            searchKinds={searchKinds}
            searchKindCount={searchKindCount}
            query={query}
          />
        ) : (
          <div className={styles.flexExpand}>
            <div className={styles.mobileOnly}>
              <TypeMobileDropdown
                selectedType={searchKindCount.name}
                searchKinds={searchKinds}
                isLoadingCounters={isLoadingCounters}
              />
            </div>
          </div>
        )}
        {isCode && showDebugScoringInfo && (
          <div>
            {`cost=${payload.metadata?.total_cost} retries=${payload.metadata?.retries} limit_reached=${payload.metadata?.limit_reached}`}
          </div>
        )}
        <div className={styles.controlsWrapper}>
          {sortItems && (
            <div className={styles.desktopOnlyMr}>
              <SortMenu onlyGroup={false} sortItems={sortItems} />
            </div>
          )}
          {loggedIn && (
            <div className={styles.desktopOnlyMr}>
              <Button onClick={openCustomScopesDialog} size="small">
                <div className={styles.saveButtonContent}>
                  <BookmarkIcon className={styles.saveBookmarkIcon} />
                  Save
                </div>
              </Button>
            </div>
          )}

          <div
            className={clsx(
              styles.filterButtonWrapper,
              (searchKindCount.total || 0) === 0
                ? styles.filterButtonWrapperHidden
                : styles.filterButtonWrapperMobileOnly,
            )}
          >
            <Button onClick={openDialog} size="small" data-testid="filter-button" leadingVisual={FilterIcon}>
              Filter
            </Button>
            {isOpen && (
              <Dialog
                title="Filters"
                renderBody={() =>
                  renderFilterDialogBody(payload, isLoading, isLoadingCounters, selectedType, searchKinds)
                }
                onClose={closeDialog}
                position={{narrow: 'bottom', wide: 'center'}}
              />
            )}
          </div>
          <div className={styles.mobileOnly}>
            <ExtraMenu
              sortItems={sortItems}
              isCode={isAnyCode}
              onClickSave={openCustomScopesDialog}
              openExperimentsDialog={() => setOpenExperimentsDialog(true)}
              refreshSearch={() => navigateToQuery(query, undefined)}
              isMobile
            />
          </div>
          {openExperimentsDialog && (
            <ExperimentsDialog
              onDismiss={() => setOpenExperimentsDialog(false)}
              onSave={() => navigateToQuery(query, undefined)}
            />
          )}

          <div className={styles.desktopOnly}>
            <ExtraMenu
              isCode={isAnyCode}
              sortItems={sortItems}
              onClickSave={openCustomScopesDialog}
              openExperimentsDialog={() => setOpenExperimentsDialog(true)}
              refreshSearch={() => navigateToQuery(query, undefined)}
              isMobile={false}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function ResultCount({
  elapsedMilliseconds,
  searchKinds,
  searchKindCount,
  isLoadingCounters,
  query,
}: {
  elapsedMilliseconds: number
  searchKinds: SearchKindCount[]
  searchKindCount: SearchKindCount
  isLoadingCounters: boolean
  query: string
}) {
  const {focusHint} = useFocusHint()
  const secondsForResults = elapsedMilliseconds >= 1000
  const isCode = searchKindCount.name === 'code'
  const unit = isCode ? 'file' : 'result'
  const resultsUnit = searchKindCount.total === 1 ? ` ${unit} ` : ` ${unit}s `
  const timeMeasurement = secondsForResults ? Math.round(elapsedMilliseconds / 1000) : elapsedMilliseconds
  const timeUnit = secondsForResults ? 's' : 'ms'
  const countText =
    (searchKindCount.mode === CountMode.Approximate ? 'About ' : '') +
    (searchKindCount.mode === CountMode.LowerBound ? 'More than ' : '') +
    getCountText(searchKindCount.total || 0) +
    resultsUnit

  return (
    <span className={styles.resultCountWrapper}>
      <div className={styles.mobileOnly}>
        <TypeMobileDropdown
          selectedType={searchKindCount.name}
          searchKinds={searchKinds}
          isLoadingCounters={isLoadingCounters}
        />
      </div>

      <span className={styles.desktopCountText}>
        <span className={styles.inlineDisplay}>{countText}</span>
        <span className={styles.timeText}>
          &nbsp;({timeMeasurement} {timeUnit})
        </span>
      </span>
      <h2
        id="search-results-count"
        className="sr-only"
        data-react-autofocus={focusHint?.startsWith(FocusHintPrefix) ? null : true}
        tabIndex={-1}
      >
        {countText}
      </h2>
      <FilterToken query={query} />
    </span>
  )
}

function FilterToken({query}: {query: string}) {
  const navigateToQuery = useNavigateToQuery()
  const astNode: NodeWithChildren = parseString(query || '') as NodeWithChildren

  // No qualifiers found
  if (!astNode.children || astNode.children.length === 0) {
    return null
  }

  // Check if query is a simple query
  const simpleQueryQualifiers = ['And', 'Or', 'Not', 'Group']
  const isSimpleQuery = astNode.children.filter(node => simpleQueryQualifiers.includes(node.kind)).length === 0
  if (!isSimpleQuery) {
    return null
  }

  const qualifierNodes = astNode.children.filter(
    node =>
      node.kind === 'Qualifier' &&
      (node.qualifier === 'Repo' ||
        node.qualifier === 'Org' ||
        node.qualifier === 'User' ||
        node.qualifier === 'Enterprise'),
  ) as QualifierNode[]

  // No repo, org, user, or enterprise qualifiers found or more than 1
  // Also - in case the qualifier is actually blank ie. `repo: ` with a space, return null
  if (!qualifierNodes || qualifierNodes.length !== 1 || qualifierNodes[0]!.content.kind === 'Nothing') {
    return null
  }

  const qualifierNode = qualifierNodes[0]!
  // Start at the beginning of the qualifier node, end at the end of content
  const qualifierStartLocation = qualifierNode.location.start
  const qualifierEndLocation = (qualifierNode.content as ContentNode).location.end
  const base = qualifierNode.qualifier === 'Enterprise' ? '/enterprises' : ''
  const href = `${base}/${(qualifierNode.content as ContentNode).value.toString()}`

  return (
    <span className={styles.filterTokenWrapper}>
      in
      <Token
        as="a"
        size="medium"
        href={href}
        text={(qualifierNode.content as ContentNode).value.toString()}
        onRemove={() => {
          const nextQuery = (query.substring(0, qualifierStartLocation) + query.substring(qualifierEndLocation)).trim()
          navigateToQuery(nextQuery, undefined)
        }}
        className={styles.filterTokenMargin}
      />
    </span>
  )
}

function renderFilterDialogBody(
  payload: SearchResults,
  isLoading: boolean,
  isLoadingCounters: boolean,
  selectedType: string | undefined,
  searchKinds: SearchKindCount[],
) {
  return (
    <div className={styles.dialogBody}>
      <FacetsDialogContent
        facets={payload.facets}
        searchType={payload.type}
        isLoading={isLoading}
        isLoadingCounters={isLoadingCounters}
        searchKinds={searchKinds}
        selectedType={selectedType}
        resultCount={payload.result_count}
      />
    </div>
  )
}

function getSortItemsForType(type: string) {
  switch (type) {
    case 'codelegacy':
      return CodeSortItems
    case 'commits':
      return CommitSortItems
    case 'discussions':
      return DiscussionSortItems
    case 'registrypackages':
      return PackageSortItems
    case 'repositories':
      return RepoSortItems
    case 'users':
      return UserSortItems
    case 'wikis':
      return WikiSortItems
    case 'issues':
      return IssueSortItems
    case 'pullrequests':
      return IssueSortItems
    default:
      return undefined
  }
}
